/**
 * FilterBar Component
 *
 * Main filter control bar for the transactions page.
 * Contains quick filters, date range picker, owner toggle, status filter, and search.
 *
 * @see types/accounting.ts for TransactionFilters and TransactionStats types
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as Popover from '@radix-ui/react-popover';
import {
  Search,
  X,
  ChevronDown,
  User,
  Building,
  Filter,
} from 'lucide-react';
import type {
  TransactionFilters,
  TransactionStats,
  Account,
  ClassificationStatus,
  Owner,
} from '@/types/accounting';
import { QuickFilters } from './QuickFilters';
import { DateRangePicker } from '../../shared/DateRangePicker';

export interface FilterBarProps {
  /** Current active filters */
  filters: TransactionFilters;
  /** Transaction statistics for badge counts */
  stats: TransactionStats;
  /** Flat list of accounts for account filter */
  accounts: Account[];
  /** Callback when filters change */
  onFilterChange: (filters: Partial<TransactionFilters>) => void;
  /** Callback to clear all filters */
  onClear: () => void;
}

/**
 * Status options for the status filter
 */
const STATUS_OPTIONS: { value: ClassificationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'rule_matched', label: 'Rule Matched' },
  { value: 'llm_classified', label: 'AI Classified' },
  { value: 'manually_classified', label: 'Manual' },
  { value: 'auto_classified', label: 'Auto' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'mortgage_payment', label: 'Mortgage' },
  { value: 'reconciliation', label: 'Reconciliation' },
];

/**
 * Owner options for the owner filter
 */
const OWNER_OPTIONS: { value: Owner | null; label: string; Icon: typeof User }[] = [
  { value: null, label: 'All Owners', Icon: User },
  { value: 'family', label: 'Family', Icon: User },
  { value: 'estate', label: 'Estate', Icon: Building },
];

/**
 * Check if any filters are active
 */
function hasActiveFilters(filters: TransactionFilters): boolean {
  return (
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.accountId !== null ||
    (filters.classificationStatus && filters.classificationStatus.length > 0) ||
    filters.owner !== null ||
    (filters.search !== null && filters.search.length > 0)
  );
}

/**
 * Owner Filter dropdown component
 */
function OwnerFilter({
  value,
  onChange,
}: {
  value: Owner | null;
  onChange: (owner: Owner | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedOption = OWNER_OPTIONS.find((opt) => opt.value === value) || OWNER_OPTIONS[0];

  const handleSelect = useCallback(
    (ownerValue: Owner | null) => {
      onChange(ownerValue);
      setOpen(false);
    },
    [onChange]
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 px-3 py-2',
            'text-sm',
            'border rounded-md shadow-sm',
            'bg-white',
            'hover:border-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            value ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid="owner-filter-trigger"
        >
          <selectedOption.Icon className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <span>{selectedOption.label}</span>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform duration-200',
              open && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1"
          sideOffset={4}
          align="start"
        >
          {OWNER_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                'hover:bg-gray-50',
                value === option.value && 'bg-blue-50 text-blue-700'
              )}
              data-testid={`owner-option-${option.value || 'all'}`}
            >
              <option.Icon className="w-4 h-4" aria-hidden="true" />
              {option.label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Status Filter dropdown component with multi-select
 */
function StatusFilter({
  value,
  onChange,
}: {
  value: ClassificationStatus[];
  onChange: (statuses: ClassificationStatus[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedCount = value.length;

  const handleToggle = useCallback(
    (status: ClassificationStatus) => {
      if (value.includes(status)) {
        onChange(value.filter((s) => s !== status));
      } else {
        onChange([...value, status]);
      }
    },
    [value, onChange]
  );

  const handleClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 px-3 py-2',
            'text-sm',
            'border rounded-md shadow-sm',
            'bg-white',
            'hover:border-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            selectedCount > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid="status-filter-trigger"
        >
          <Filter className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <span>
            Status
            {selectedCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                {selectedCount}
              </span>
            )}
          </span>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform duration-200',
              open && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1"
          sideOffset={4}
          align="start"
        >
          {selectedCount > 0 && (
            <>
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-3 py-2 text-sm text-left text-gray-500 hover:bg-gray-50"
              >
                Clear selection
              </button>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}
          {STATUS_OPTIONS.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleToggle(option.value)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                  'hover:bg-gray-50',
                  isSelected && 'bg-blue-50'
                )}
                data-testid={`status-option-${option.value}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(option.value)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
                {option.label}
              </button>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Account Filter dropdown component
 */
function AccountFilter({
  accounts,
  value,
  onChange,
}: {
  accounts: Account[];
  value: number | null;
  onChange: (accountId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filter to bank accounts (assets)
  const bankAccounts = accounts.filter((a) => a.accountType === 'asset');

  // Filter by search
  const filteredAccounts = search
    ? bankAccounts.filter(
        (a) =>
          a.name.toLowerCase().includes(search.toLowerCase()) ||
          a.fullPath.toLowerCase().includes(search.toLowerCase())
      )
    : bankAccounts;

  const selectedAccount = accounts.find((a) => a.id === value);

  const handleSelect = useCallback(
    (accountId: number | null) => {
      onChange(accountId);
      setOpen(false);
      setSearch('');
    },
    [onChange]
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 px-3 py-2',
            'text-sm',
            'border rounded-md shadow-sm',
            'bg-white',
            'hover:border-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            value ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid="account-filter-trigger"
        >
          <span className="truncate max-w-32">
            {selectedAccount ? selectedAccount.name : 'All Accounts'}
          </span>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform duration-200',
              open && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          sideOffset={4}
          align="start"
        >
          {/* Search */}
          <div className="p-2 border-b border-gray-200">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded">
              <Search className="w-4 h-4 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search accounts..."
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="p-0.5 rounded hover:bg-gray-200"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Account list */}
          <div className="max-h-60 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={clsx(
                'w-full px-3 py-2 text-sm text-left',
                'hover:bg-gray-50',
                value === null && 'bg-blue-50 text-blue-700'
              )}
            >
              All Accounts
            </button>
            {filteredAccounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => handleSelect(account.id)}
                className={clsx(
                  'w-full px-3 py-2 text-sm text-left truncate',
                  'hover:bg-gray-50',
                  value === account.id && 'bg-blue-50 text-blue-700'
                )}
                title={account.fullPath}
              >
                {account.name}
              </button>
            ))}
            {filteredAccounts.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No accounts found
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Search input with debounce
 */
function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (search: string) => void;
}) {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local value with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounce changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        onChange(newValue);
      }, 300);
    },
    [onChange]
  );

  // Clear search
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle escape to clear
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' && localValue) {
        e.preventDefault();
        handleClear();
      }
    },
    [localValue, handleClear]
  );

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <div className="relative flex-1 min-w-48 max-w-xs">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Search transactions..."
        className={clsx(
          'w-full pl-9 pr-8 py-2 text-sm',
          'border rounded-md shadow-sm',
          'bg-white border-gray-300',
          'placeholder:text-gray-400',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
        )}
        aria-label="Search transactions"
        data-testid="search-input"
      />
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
          aria-label="Clear search"
        >
          <X className="w-3 h-3 text-gray-400" />
        </button>
      )}
      <span className="hidden sm:inline absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400">
        {!localValue && (
          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">
            {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+K
          </kbd>
        )}
      </span>
    </div>
  );
}

/**
 * FilterBar renders the complete filter control bar.
 *
 * Features:
 * - Quick filter presets (Needs Attention, This Month, etc.)
 * - Date range picker
 * - Owner filter (Family/Estate)
 * - Status multi-select
 * - Search with debounce (300ms)
 * - Keyboard shortcuts (Cmd+K for search)
 * - Clear all button
 *
 * @example
 * ```tsx
 * <FilterBar
 *   filters={filters}
 *   stats={stats}
 *   accounts={accounts}
 *   onFilterChange={handleFilterChange}
 *   onClear={handleClearFilters}
 * />
 * ```
 */
export function FilterBar({
  filters,
  stats,
  accounts,
  onFilterChange,
  onClear,
}: FilterBarProps) {
  const hasFilters = hasActiveFilters(filters);

  const handleDateChange = useCallback(
    (from: string | null, to: string | null) => {
      onFilterChange({ dateFrom: from, dateTo: to });
    },
    [onFilterChange]
  );

  const handleOwnerChange = useCallback(
    (owner: Owner | null) => {
      onFilterChange({ owner });
    },
    [onFilterChange]
  );

  const handleStatusChange = useCallback(
    (classificationStatus: ClassificationStatus[]) => {
      onFilterChange({ classificationStatus });
    },
    [onFilterChange]
  );

  const handleAccountChange = useCallback(
    (accountId: number | null) => {
      onFilterChange({ accountId });
    },
    [onFilterChange]
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      onFilterChange({ search: search || null });
    },
    [onFilterChange]
  );

  return (
    <div
      className="space-y-3 p-4 bg-white border-b border-gray-200"
      data-testid="filter-bar"
    >
      {/* Quick filters row */}
      <QuickFilters
        currentFilters={filters}
        stats={stats}
        onSelect={onFilterChange}
      />

      {/* Filter controls row */}
      <div className="flex flex-wrap gap-3 items-center">
        <DateRangePicker
          from={filters.dateFrom}
          to={filters.dateTo}
          onChange={handleDateChange}
        />

        <AccountFilter
          accounts={accounts}
          value={filters.accountId}
          onChange={handleAccountChange}
        />

        <OwnerFilter
          value={filters.owner}
          onChange={handleOwnerChange}
        />

        <StatusFilter
          value={filters.classificationStatus || []}
          onChange={handleStatusChange}
        />

        <SearchInput
          value={filters.search || ''}
          onChange={handleSearchChange}
        />

        {hasFilters && (
          <button
            type="button"
            onClick={onClear}
            className={clsx(
              'px-3 py-2 text-sm',
              'text-gray-500 hover:text-gray-700',
              'hover:bg-gray-100 rounded-md',
              'focus:outline-none focus:ring-2 focus:ring-blue-500'
            )}
            data-testid="clear-filters"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

export default FilterBar;
